import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { describeTarget, flush, startView, track, trackDwell } from '@/lib/analytics';
import { useUi } from '@/stores/ui';

/**
 * The whole instrumentation layer, in one place.
 *
 *  - one delegated click listener, reading data-ee-* off the DOM, so adding a feature means
 *    adding an attribute rather than wiring a callback
 *  - one shared IntersectionObserver for viewport dwell, paused when the tab hides
 *  - PerformanceObserver for LCP, CLS and INP
 *
 * Elements opt in by carrying data-ee-feature (controls) or data-ee-advertiser (cards).
 */
export default function AnalyticsRoot() {
  const location = useLocation();

  useEffect(() => {
    startView();
    track('interact', 'view', {});
  }, [location.pathname, location.search]);

  useEffect(() => {
    function onClick(event: MouseEvent) {
      const described = describeTarget(event.target as Element);
      if (!described.name) return;
      const { name, ...detail } = described;
      track('interact', name, detail);
    }

    document.addEventListener('click', onClick, { capture: true });
    return () => document.removeEventListener('click', onClick, { capture: true });
  }, []);

  // Viewport dwell. Timers pause when the tab hides, so a backgrounded tab cannot inflate it.
  useEffect(() => {
    const started = new Map<Element, number>();

    const stop = (element: Element) => {
      const at = started.get(element);
      if (at === undefined) return;
      started.delete(element);
      const target = element as HTMLElement;
      trackDwell(performance.now() - at, {
        advertiser: target.dataset.eeAdvertiser,
        listing: target.dataset.eeListing,
        photo: target.dataset.eePhoto,
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) started.set(entry.target, performance.now());
          else stop(entry.target);
        }
      },
      { threshold: 0.5 },
    );

    const attach = () => {
      for (const element of document.querySelectorAll('[data-ee-advertiser]')) {
        observer.observe(element);
      }
    };
    attach();

    // Cards arrive as the feed scrolls, so watch for new ones rather than observing once.
    const mutations = new MutationObserver(attach);
    mutations.observe(document.body, { childList: true, subtree: true });

    const stopAll = () => {
      for (const element of [...started.keys()]) stop(element);
      flush();
    };

    /*
      Start measuring again what is still on screen. An IntersectionObserver reports a change of
      intersection, not a state, so after `stopAll` an element that never left the viewport would
      never be heard from again: un-observing and observing again is what makes it report where
      it stands now. Scoped, because restarting the cards under an open viewer is the double count
      the viewer pause exists to prevent.
    */
    const resume = (scope: ParentNode) => {
      for (const element of scope.querySelectorAll('[data-ee-advertiser]')) {
        observer.unobserve(element);
        observer.observe(element);
      }
    };
    const viewerOpen = () => useUi.getState().viewer !== null;
    const resumeVisible = () => {
      const surface = viewerOpen() ? document.querySelector('.ee-viewer') : document;
      if (surface) resume(surface);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') stopAll();
      else resumeVisible();
    };
    document.addEventListener('visibilitychange', onVisibility);

    /*
      The photo viewer covers the page, and IntersectionObserver measures geometry rather than
      occlusion: every card behind it stays "visible" and keeps accruing. Worse, a photograph in
      the viewer and its own thumbnail underneath would both be counted. Opening the viewer is
      therefore treated exactly like hiding the tab — everything stops, and the viewer's own
      image starts fresh when the observer picks it up. Each step inside it stops the photograph
      being left and measures the one arrived at; closing it measures the page again.
    */
    const unsubscribe = useUi.subscribe((state, previous) => {
      if (state.viewer === previous.viewer) return;
      stopAll();
      // After the render that follows, so the viewer's new photograph — or the page it uncovered —
      // is what gets measured.
      requestAnimationFrame(resumeVisible);
    });
    window.addEventListener('pagehide', flush);

    return () => {
      observer.disconnect();
      mutations.disconnect();
      unsubscribe();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
    };
  }, []);

  // Core Web Vitals, reported into the same pipeline as everything else.
  useEffect(() => {
    if (typeof PerformanceObserver === 'undefined') return;
    const observers: PerformanceObserver[] = [];

    const observe = (type: string, handle: (entry: PerformanceEntry) => void) => {
      try {
        const observer = new PerformanceObserver((list) => list.getEntries().forEach(handle));
        // `durationThreshold` is only read for 'event', and its default is 104ms — so without
        // it the 40ms floor below was never reached, and every interaction between the two
        // went unreported.
        observer.observe({ type, buffered: true, durationThreshold: 40 } as PerformanceObserverInit);
        observers.push(observer);
      } catch {
        // A browser without this entry type simply reports less.
      }
    };

    observe('largest-contentful-paint', (entry) =>
      track('perf', 'LCP', { value: Math.round(entry.startTime) }),
    );
    observe('layout-shift', (entry) => {
      const shift = entry as PerformanceEntry & { value: number; hadRecentInput: boolean };
      if (shift.hadRecentInput || shift.value < 0.01) return;
      track('perf', 'CLS', { value: Math.round(shift.value * 1000) / 1000 });
    });
    observe('event', (entry) => {
      const timing = entry as PerformanceEntry & { interactionId?: number };
      if (!timing.interactionId || entry.duration < 40) return;
      track('perf', 'INP', { value: Math.round(entry.duration) });
    });

    return () => observers.forEach((observer) => observer.disconnect());
  }, []);

  return null;
}
